import React, { useState, useEffect } from 'react';
import { Heart, ChevronDown, ChevronUp, ChevronRight, Package, Tag, ArrowLeftRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ModelViewer from './ModelViewer';
import PieceActivityAndHolders from './PieceActivityAndHolders';
import { normalizePieces } from '../utils/pieceNormalizer';
import { getCategoryDisplayName } from '../utils/pieceUtils';
import { getCapsuleForPiece } from '../utils/supabaseDatabase';
import { trackPieceView } from '../utils/supabaseViewTracking';
import VerifiedBadge from './VerifiedBadge';

/**
 * Grouped Pieces Modal - EXACT copy of regular piece detail modal
 * but with quantity selector to choose which piece to buy
 */
const GroupedPiecesModal = ({ isOpen, onClose, groupedCard, onBuyNow, onMakeOffer, onRequestTrade, solPrice = 195.84, wallet, publicKey, isConnected }) => {
  const navigate = useNavigate();
  const [selectedPieceIndex, setSelectedPieceIndex] = useState(0);
  const [showPieceSelector, setShowPieceSelector] = useState(false);
  const [modalViewMode, setModalViewMode] = useState('3d');
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null);
  
  // Track which piece was last viewed to prevent double-tracking
  const lastTrackedPiece = React.useRef(null);
  
  // Set initial piece index to show the user's owned edition if they own one
  useEffect(() => {
    if (!isOpen || !groupedCard) return;
    
    const pieces = groupedCard._groupPieces || [groupedCard];
    
    // If user owns one of these pieces, prioritize showing their owned edition
    if (publicKey) {
      const ownedIndex = pieces.findIndex(p => 
        p.holder_wallet === publicKey.toString()
      );
      if (ownedIndex >= 0) {
        console.log('📍 [GROUPED MODAL] User owns piece at index:', ownedIndex);
        setSelectedPieceIndex(ownedIndex);
        return;
      }
    }
    
    // Otherwise, reset to first piece
    setSelectedPieceIndex(0);
  }, [isOpen, groupedCard, publicKey]);

  // Check if piece belongs to a capsule when modal opens or piece changes
  // Also track view when piece is viewed
  useEffect(() => {
    if (!isOpen || !groupedCard) {
      setPieceCapsuleInfo(null);
      return;
    }

    const pieces = groupedCard._groupPieces || [groupedCard];
    const selectedPiece = pieces[selectedPieceIndex] || pieces[0];
    
    // Track piece view (only if not already tracked in this session)
    if (selectedPiece?.id && lastTrackedPiece.current !== String(selectedPiece.id)) {
      const walletAddress = publicKey?.toString() || null;
      const pieceIdString = String(selectedPiece.id);
      
      trackPieceView(pieceIdString, walletAddress).then(result => {
        if (result.success) {
          console.log('✅ [GROUPED MODAL] View tracked:', result.stats);
          lastTrackedPiece.current = pieceIdString;
        } else {
          console.log('⏭️ [GROUPED MODAL] View not tracked:', result.error);
          // Still mark as tracked to avoid repeated failed attempts
          lastTrackedPiece.current = pieceIdString;
        }
      });
    }

    const checkCapsule = async () => {
      if (selectedPiece?.id) {
        try {
          let originalId = selectedPiece.id;
          const itemIdString = String(selectedPiece.id);
          console.log('🔍 GROUPED MODAL - CAPSULE CHECK: Starting for piece ID:', selectedPiece.id);
          
          if (itemIdString.startsWith('supabase_')) {
            originalId = itemIdString.replace('supabase_', '');
            if (!isNaN(originalId)) {
              originalId = parseInt(originalId, 10);
            }
          } else if (itemIdString.startsWith('local_')) {
            console.log('🔍 Local piece detected, skipping capsule check');
            setPieceCapsuleInfo(null);
            return;
          }
          
          console.log('🔍 Checking capsule for piece:', { originalId, transformedId: selectedPiece.id });
          const capsuleInfo = await getCapsuleForPiece(originalId);
          console.log('🔍 Capsule info result:', capsuleInfo);
          
          if (capsuleInfo) {
            console.log('✅ CAPSULE FOUND! Setting pieceCapsuleInfo:', capsuleInfo);
          } else {
            console.log('ℹ️ No capsule found for this piece');
          }
          
          setPieceCapsuleInfo(capsuleInfo);
        } catch (error) {
          console.error('❌ Error fetching capsule info:', error);
          setPieceCapsuleInfo(null);
        }
      } else {
        console.log('⚠️ No piece ID found');
        setPieceCapsuleInfo(null);
      }
    };

    checkCapsule();
  }, [isOpen, groupedCard, selectedPieceIndex, publicKey]);

  if (!isOpen || !groupedCard) return null;

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
      onClose(); // Close modal after navigation
    }
  };

  // Handle view entire capsule button click
  const handleViewCapsule = () => {
    if (pieceCapsuleInfo?.id) {
      navigate(`/capsule/${pieceCapsuleInfo.id}`);
      onClose();
    }
  };

  const pieces = groupedCard._groupPieces || [groupedCard];
  // Show ALL pieces in selector, not just available ones
  const allPieces = pieces;
  const availableCount = pieces.filter(p => 
    p.listing_status === 'listed' || 
    (p.is_public && !p.is_draft && p.mint_address)
  ).length;
  
  const selectedPiece = allPieces[selectedPieceIndex] || pieces[0];
  const normalizedPiece = selectedPiece ? normalizePieces([selectedPiece])[0] : null;
  const hasMultiplePieces = allPieces.length > 1;
  const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
  
  // Debug: Log IDs to troubleshoot activity/holders
  console.log('🖼️ GroupedPiecesModal - Selected Piece IDs:', {
    id: selectedPiece?.id,
    databaseId: selectedPiece?.databaseId,
    mint_address: selectedPiece?.mint_address,
    mintAddress: selectedPiece?.mintAddress,
    inDatabase: selectedPiece?.inDatabase,
    isCreated: selectedPiece?.isCreated
  });
  console.log('📋 Passing to PieceActivityAndHolders:', {
    pieceId: selectedPiece?.databaseId || selectedPiece?.id,
    allPieceIds: allPieces.map(p => ({ id: p.id, databaseId: p.databaseId, mint: p.mint_address }))
  });

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
        style={{ overflow: 'visible' }}
      >
        <div className="flex flex-1 rounded-2xl" style={{ position: 'relative', zIndex: 1, overflow: 'visible' }}>
          {/* Left side - Thumbnail/3D Model */}
          <div className="w-1/2 p-6 overflow-y-auto" style={{ maxHeight: '90vh' }}>
            {/* 3D Model Toggle Buttons */}
            {has3DModel && (
              <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalViewMode('3d');
                  }}
                  className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    modalViewMode === '3d'
                      ? 'text-white bg-[#2a2a2a] shadow-lg' 
                      : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                  }`}
                >
                  3D Model
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalViewMode('thumbnail');
                  }}
                  className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    modalViewMode === 'thumbnail'
                      ? 'text-white bg-[#2a2a2a] shadow-lg' 
                      : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                  }`}
                >
                  Thumbnail
                </button>
              </div>
            )}
            
            <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
              {modalViewMode === '3d' && has3DModel ? (
                <ModelViewer
                  src={normalizedPiece.previewUrl}
                  posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                  className="w-full h-full modal"
                />
              ) : (
                <img 
                  src={selectedPiece.thumbnail_url || selectedPiece.thumbnailUrl || '/images/piece-placeholder.png'} 
                  alt={selectedPiece.name}
                  className="w-full h-full object-cover rounded-xl"
                />
              )}
            </div>

            {/* Creator Section */}
            <div className="space-y-4 mb-6">
              <div>
                <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                  Original Creator
                </div>
                <button 
                  onClick={() => handleAccountClick(selectedPiece)}
                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                    {(selectedPiece?.originalCreator?.profilePicture || selectedPiece?.creator?.profilePicture) ? (
                      <img
                        src={selectedPiece.originalCreator?.profilePicture || selectedPiece.creator?.profilePicture}
                        alt={selectedPiece.originalCreator?.username || selectedPiece.creator?.username || 'Creator'}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {(selectedPiece?.originalCreator?.username || selectedPiece?.creator?.username || 'Creator').charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                        {selectedPiece?.originalCreator?.username || selectedPiece?.creator?.username || 'Original Creator'}
                      </div>
                      {(selectedPiece?.originalCreator?.verified || selectedPiece?.creator?.verified) && (
                        <VerifiedBadge 
                          size="sm"
                          verificationType={selectedPiece?.originalCreator?.verificationType || selectedPiece?.creator?.verificationType || 'none'}
                        />
                      )}
                    </div>
                    <div className="text-gray-400 text-xs mt-0.5">
                      {selectedPiece?.originalCreator?.piecesCount || selectedPiece?.creator?.piecesCount || '0'} pieces created
                    </div>
                  </div>
                  <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </div>
                </button>

                {/* Creator Royalty - Outside button */}
                {selectedPiece?.royalty_bps !== undefined && selectedPiece?.royalty_bps !== null && (
                  <div className="mt-3 px-1">
                    <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                      {(selectedPiece.royalty_bps / 100).toFixed(1)}% Creator Royalty
                    </span>
                  </div>
                )}

                {/* Social Links */}
                {(() => {
                  // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                  const socialLinks = pieceCapsuleInfo 
                    ? {
                        website: pieceCapsuleInfo.website,
                        twitter: pieceCapsuleInfo.twitter,
                        instagram: pieceCapsuleInfo.instagram,
                        discord: pieceCapsuleInfo.discord
                      }
                    : (selectedPiece?.social_links || {});
                  const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                  
                  // DEBUG: Log social links data
                  console.log('🔍 [GROUPED MODAL] Social Links Debug:', {
                    pieceId: selectedPiece?.id,
                    pieceName: selectedPiece?.name,
                    pieceCapsuleInfo: pieceCapsuleInfo,
                    usingCapsuleLinks: !!pieceCapsuleInfo,
                    social_links: socialLinks,
                    hasSocialLinks
                  });
                  
                  if (!hasSocialLinks) return null;
                  
                  return (
                    <div className="flex items-center gap-2 mt-3 px-1">
                      {socialLinks.website && (
                        <a
                          href={socialLinks.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Website"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.twitter && (
                        <a
                          href={socialLinks.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="X (Twitter)"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.instagram && (
                        <a
                          href={socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Instagram"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.discord && (
                        <a
                          href={socialLinks.discord}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Discord"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Right side - Details */}
          <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto" style={{ maxHeight: '90vh' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-white">
                    {groupedCard.name}
                  </h2>
                  <button
                    className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                  >
                    <Heart 
                      size={20} 
                      className="text-gray-400 hover:text-red-500 transition-all duration-300" 
                    />
                  </button>
                </div>
                {/* Custom Tags */}
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedPiece?.tags && selectedPiece.tags.length > 0 ? (
                    selectedPiece.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">No tags</span>
                  )}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActivityPanelOpen(!activityPanelOpen);
                }}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  activityPanelOpen 
                    ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                    : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                }`}
              >
                Activity
              </button>
            </div>

            <div className="space-y-5">
              {/* Quantity Selector (NEW - only shown if multiple pieces) */}
              {hasMultiplePieces && (
                <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                  <div className="mb-2">
                    <span className="text-gray-400 text-sm font-semibold">Select Piece</span>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPieceSelector(!showPieceSelector);
                    }}
                    className="w-full flex items-center justify-between p-3 bg-[#111111] hover:bg-[#1a1a1a] rounded-lg transition-all border border-[#2a2a2a]"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium">Piece #{selectedPieceIndex + 1}</span>
                      <span className="text-xs text-gray-500 font-mono">
                        {selectedPiece.mint_address?.slice(0, 8)}...
                      </span>
                    </div>
                    {showPieceSelector ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </button>

                  {showPieceSelector && (
                    <div className="mt-2 max-h-40 overflow-y-auto bg-[#111111] rounded-lg border border-[#2a2a2a]">
                      {allPieces.map((piece, index) => (
                        <button
                          key={piece.id || index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPieceIndex(index);
                            setShowPieceSelector(false);
                          }}
                          className={`w-full flex items-center justify-between p-2.5 hover:bg-[#1a1a1a] transition-all text-left ${
                            index === selectedPieceIndex ? 'bg-[#1a1a1a]' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm">Piece #{index + 1}</span>
                            <span className="text-xs text-gray-500 font-mono">
                              {piece.mint_address?.slice(0, 6)}...{piece.mint_address?.slice(-4)}
                            </span>
                          </div>
                          {index === selectedPieceIndex && (
                            <span className="text-green-400 text-xs font-medium">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Category and Color */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span className="text-gray-300 text-sm font-medium">
                      {getCategoryDisplayName(selectedPiece?.category) || 'Uncategorized'}
                    </span>
                  </div>
                  {selectedPiece?.color && (
                    <div className="text-gray-300 text-sm">
                      <span className="text-gray-500">Color: </span>
                      <span className="capitalize font-medium">{selectedPiece.color}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {selectedPiece?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                </p>
              </div>

              {/* Price */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Price</span>
                    <div className="text-green-400 font-bold text-xl">
                      {(() => {
                        const isOwnedByCurrentUser = publicKey && selectedPiece?.holder_wallet === publicKey.toString();
                        const isListed = selectedPiece?.listing_status === 'listed';
                        
                        let displayPrice;
                        // If user owns this piece
                        if (isOwnedByCurrentUser) {
                          // If they've listed it, show THEIR listing price
                          if (isListed && selectedPiece?.listing_price) {
                            displayPrice = selectedPiece.listing_price;
                          }
                          // If not listed, show purchase price
                          else if (!isListed && selectedPiece?.last_sale_price_lamports) {
                            displayPrice = selectedPiece.last_sale_price_lamports / 1_000_000_000;
                          }
                          else {
                            displayPrice = selectedPiece?.price || 0;
                          }
                        } else {
                          // For pieces not owned by user, show lowest available price
                          displayPrice = selectedPiece?.lowestPrice ?? selectedPiece?.listing_price ?? selectedPiece?.price;
                        }
                        
                        // Format price to remove trailing zeros
                        const formatted = typeof displayPrice === 'number' 
                          ? displayPrice.toFixed(4).replace(/\.?0+$/, '')
                          : displayPrice;
                        return formatted;
                      })()} SOL
                    </div>
                  </div>
                  <div className="text-right text-gray-500 text-sm">
                    ≈ ${(() => {
                      const isOwnedByCurrentUser = publicKey && selectedPiece?.holder_wallet === publicKey.toString();
                      const isListed = selectedPiece?.listing_status === 'listed';
                      
                      let displayPrice;
                      if (isOwnedByCurrentUser) {
                        if (isListed && selectedPiece?.listing_price) {
                          displayPrice = selectedPiece.listing_price;
                        } else if (!isListed && selectedPiece?.last_sale_price_lamports) {
                          displayPrice = selectedPiece.last_sale_price_lamports / 1_000_000_000;
                        } else {
                          displayPrice = selectedPiece?.price || 0;
                        }
                      } else {
                        displayPrice = selectedPiece?.lowestPrice ?? selectedPiece?.listing_price ?? selectedPiece?.price;
                      }
                      return ((displayPrice || 0) * solPrice).toFixed(2);
                    })()} USD
                  </div>
                </div>
              </div>

              {/* Scarcity */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                <div className="text-white font-semibold text-lg">
                  {groupedCard._groupSize || allPieces.length}
                </div>
              </div>

              {/* Dropped Date */}
              <div className="text-gray-500 text-sm pt-2">
                Dropped {selectedPiece?.createdAt ? new Date(selectedPiece.createdAt).toLocaleDateString('en-US', { 
                  year: '2-digit', 
                  month: '2-digit', 
                  day: '2-digit' 
                }) : selectedPiece?.created_at ? new Date(selectedPiece.created_at).toLocaleDateString('en-US', { 
                  year: '2-digit', 
                  month: '2-digit', 
                  day: '2-digit' 
                }) : 'Recently'}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 pb-2">
                {(() => {
                  const isOwner = isConnected && publicKey && 
                    selectedPiece?.holder_wallet === publicKey.toString();
                  
                  // Check if piece is owned by the original creator (primary sale)
                  const creatorWallet = selectedPiece?.creator_wallet || 
                                       selectedPiece?.creators?.wallet ||
                                       selectedPiece?.originalCreator?.wallet;
                  const isOwnedByCreator = selectedPiece?.holder_wallet === creatorWallet;
                  
                  return (
                    <>
                      {/* OWNERSHIP BADGE: Show when user owns the piece */}
                      {isOwner && (
                        <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-xl text-center border border-green-600/30 mb-2">
                          <span className="text-sm font-medium">✓ You own this piece</span>
                        </div>
                      )}
                      
                      {/* LIST FOR SALE BUTTON: Show when owner owns piece and it's not listed */}
                      {isOwner && selectedPiece?.listing_status !== 'listed' && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/listings');
                            onClose();
                          }}
                          className="w-full px-4 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white rounded-xl transition-all duration-200 font-medium text-sm border border-[#2a2a2a] flex items-center justify-center gap-2"
                        >
                          List for Sale
                        </button>
                      )}
                      
                      {/* PRIMARY SALE: Buy directly from creator (no listing needed) */}
                      {!isOwner && isOwnedByCreator && 
                       selectedPiece?.mint_address && 
                       selectedPiece?.listing_status !== 'sold' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onBuyNow && onBuyNow(selectedPiece);
                          }}
                          className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                        >
                          Buy Now - {(selectedPiece?.listing_price || selectedPiece?.price || 0).toFixed(4)} SOL
                        </button>
                      )}
                      
                      {/* SECONDARY SALE: Buy from secondary market listing */}
                      {!isOwner && !isOwnedByCreator && 
                       selectedPiece?.listing_status === 'listed' && 
                       selectedPiece?.mint_address && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onBuyNow && onBuyNow(selectedPiece);
                          }}
                          className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                        >
                          Buy Now - {(selectedPiece?.listing_price || selectedPiece?.price || 0).toFixed(4)} SOL
                        </button>
                      )}
                      
                      {/* SOLD OUT: Show when piece is sold */}
                      {selectedPiece?.listing_status === 'sold' && (
                        <div className="w-full px-4 py-3 bg-red-600/20 text-red-400 rounded-xl text-center border border-red-600/30">
                          <span className="text-sm font-medium">Sold Out</span>
                        </div>
                      )}
                      
                      {/* Offer & Trade buttons - shown for non-owners */}
                      {!isOwner && selectedPiece?.mint_address && selectedPiece?.listing_status !== 'sold' && (
                        <div className="grid grid-cols-2 gap-3">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              onMakeOffer && onMakeOffer(selectedPiece);
                            }}
                            className="px-4 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white rounded-xl transition-all duration-200 font-medium text-sm border border-[#2a2a2a]"
                          >
                            Make Offer
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              onRequestTrade && onRequestTrade(selectedPiece);
                            }}
                            className="px-4 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white rounded-xl transition-all duration-200 font-medium text-sm border border-[#2a2a2a] flex items-center justify-center gap-2"
                          >
                            <ArrowLeftRight size={14} />
                            Request Trade
                          </button>
                        </div>
                      )}

                      {/* View Entire Capsule Button - Only show if piece belongs to a capsule */}
                      {pieceCapsuleInfo && (
                        <div className="pt-3">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCapsule();
                            }}
                            className="w-full px-4 py-2.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3a3a3a] hover:border-[#4a4a4a] text-gray-300 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
                          >
                            <Package size={14} />
                            View entire capsule
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Activity Side Panel - slides out from behind modal */}
          <div 
            className="absolute top-0 left-full h-full w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ 
              transform: activityPanelOpen ? 'translateX(0)' : 'translateX(-100%)',
              zIndex: activityPanelOpen ? 50 : 0,
              opacity: activityPanelOpen ? 1 : 0,
              visibility: activityPanelOpen ? 'visible' : 'hidden',
              pointerEvents: activityPanelOpen ? 'auto' : 'none'
            }}
          >
            <div className="h-full flex flex-col p-5">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                <h3 className="text-base font-bold text-white">Activity</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivityPanelOpen(false);
                  }}
                  className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                  title="Close Activity Panel"
                >
                  <ChevronRight size={18} className="text-gray-400" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <PieceActivityAndHolders
                  pieceId={selectedPiece?.databaseId || selectedPiece?.id}
                  pieceName={selectedPiece?.name}
                  basePrice={parseFloat(selectedPiece?.price) || 0}
                  solPrice={solPrice || 150}
                  totalQuantity={allPieces.length}
                  groupedPieceIds={allPieces.map(p => p.id)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupedPiecesModal;
